import { formatComplianceHealthTime } from "../../lib/complianceIntegrationHealth.js";
import { sourceHealth, whenLabel } from "./model.js";
import { Fact, SourceLogo, StatusPill, TONE } from "../../components/statusCard.jsx";
import Button from "../../components/ui/Button.jsx";
import { SkeletonLines } from "../../components/ui/Skeleton.jsx";

function latest(values) {
    return values.filter(Boolean).sort().at(-1);
}

function SourceFacts({ sourceKey, data }) {
    switch (sourceKey) {
        case "anthropic":
            return data.configured ? (
                <dl className="m-0 grid grid-cols-3 gap-[12px]">
                    <Fact label="Last check">{formatComplianceHealthTime(data.lastCheckedAt)}</Fact>
                    <Fact label="Last success">{formatComplianceHealthTime(data.lastSuccessAt)}</Fact>
                    <Fact label="Last failure">{formatComplianceHealthTime(data.lastErrorAt)}</Fact>
                </dl>
            ) : null;
        case "openai": {
            if (data.length === 0) return null;
            const lastPoll = latest(data.map((integration) => integration.lastPollAt));
            return (
                <dl className="m-0 grid grid-cols-2 gap-[12px]">
                    <Fact label="Workspaces">{data.length}</Fact>
                    <Fact label="Last sync">{lastPoll ? whenLabel(lastPoll) : "None yet"}</Fact>
                </dl>
            );
        }
        case "perplexity":
            return data.configured ? (
                <dl className="m-0 grid grid-cols-2 gap-[12px]">
                    <Fact label="Delivery">Webhook</Fact>
                    <Fact label="Retention">{data.rawRetentionDays} days</Fact>
                </dl>
            ) : null;
        case "langsmith": {
            if (data.length === 0) return null;
            const watched = data.reduce((sum, integration) => sum + integration.watchedProjects.length, 0);
            const lastPoll = latest(data.map((integration) => integration.lastPollAt));
            return (
                <dl className="m-0 grid grid-cols-3 gap-[12px]">
                    <Fact label="Workspaces">{data.length}</Fact>
                    <Fact label="Projects">{watched}</Fact>
                    <Fact label="Last delivery">{lastPoll ? whenLabel(lastPoll) : "None yet"}</Fact>
                </dl>
            );
        }
        case "copilot": {
            if (data.length === 0) return null;
            const lastSuccess = latest(data.map((integration) => integration.lastSuccessAt));
            return (
                <dl className="m-0 grid grid-cols-2 gap-[12px]">
                    <Fact label="Microsoft tenants">{data.length}</Fact>
                    <Fact label="Last sync">{lastSuccess ? whenLabel(lastSuccess) : "None yet"}</Fact>
                </dl>
            );
        }
        case "vertex": {
            if (data.length === 0) return null;
            const lastSuccess = latest(data.map((integration) => integration.lastSuccessAt));
            return (
                <dl className="m-0 grid grid-cols-2 gap-[12px]">
                    <Fact label="Google Cloud orgs">{data.length}</Fact>
                    <Fact label="Last read">{lastSuccess ? whenLabel(lastSuccess) : "None yet"}</Fact>
                </dl>
            );
        }
        case "foundry": {
            const integrations = data.integrations ?? [];
            if (integrations.length === 0) return null;
            const workspaces = integrations.reduce((sum, integration) => sum + integration.workspaces.length, 0);
            const lastSuccess = latest(integrations.map((integration) => integration.lastSuccessAt));
            return (
                <dl className="m-0 grid grid-cols-3 gap-[12px]">
                    <Fact label="Microsoft tenants">{integrations.length}</Fact>
                    <Fact label="Workspaces">{workspaces}</Fact>
                    <Fact label="Last read">{lastSuccess ? whenLabel(lastSuccess) : "None yet"}</Fact>
                </dl>
            );
        }
        case "litellm": {
            if (data.length === 0) return null;
            const lastDelivery = latest(data.map((integration) => integration.lastDeliveryAt));
            return (
                <dl className="m-0 grid grid-cols-2 gap-[12px]">
                    <Fact label="Connections">{data.length}</Fact>
                    <Fact label="Last delivery">{lastDelivery ? whenLabel(lastDelivery) : "None yet"}</Fact>
                </dl>
            );
        }
        default:
            return null;
    }
}

function SetupPreview({ sourceKey }) {
    const steps = {
        anthropic: ["Create a Compliance Access Key in claude.ai", "Paste it here", "Confirm the first sync"],
        openai: ["Create a Compliance Logs Platform key", "Paste it with the workspace ID", "Confirm the first sync"],
        perplexity: ["Create the webhook here", "Add its URL and token in Perplexity"],
        litellm: ["Name the connection", "Add Origin to your LiteLLM config", "Confirm the first call arrives"],
        langsmith: [
            "Create a service key in LangSmith",
            "Paste it with the workspace ID",
            "Choose projects to watch",
            "Confirm the first run arrives",
        ],
        copilot: [
            "Choose how Origin connects to Microsoft",
            "Grant admin consent, or paste your app registration",
            "Confirm capture starts",
        ],
        vertex: [
            "Name the org and the dataset",
            "Send Vertex AI audit logs to BigQuery",
            "Create Origin's service account",
            "Paste the key and connect",
            "Prompt logging turns on",
            "Confirm the first read",
        ],
        foundry: [
            "Choose how Origin connects to Microsoft",
            "Grant admin consent, or paste your app registration",
            "Grant the role and add a workspace",
            "Send gateway logs or agent traces to it",
            "Confirm capture starts",
        ],
    }[sourceKey];
    if (!steps) return null;
    return (
        <ol className="m-0 grid list-decimal gap-[2px] pl-[18px] text-[12.5px] leading-[1.6] text-[color:var(--muted)]">
            {steps.map((step) => (
                <li key={step}>{step}</li>
            ))}
        </ol>
    );
}

function HealthCard({ source, entry, health, onOpen, onRetry }) {
    const data = entry?.data;
    const setup = health.tone === TONE.SETUP;
    const needsWork = health.tone === TONE.ATTENTION || health.tone === TONE.WAITING;
    const loading = health.tone === TONE.LOADING;
    return (
        <article
            aria-busy={entry?.loading ? "true" : undefined}
            aria-labelledby={`data-source-${source.key}-title`}
            className={[
                "flex flex-col gap-[14px] rounded-[10px] border p-[16px]",
                setup
                    ? "border-dashed border-[color:var(--line-strong)] bg-transparent"
                    : "bg-[color:var(--panel)]",
                health.tone === TONE.ATTENTION
                    ? "border-[color:var(--danger)]"
                    : setup
                      ? ""
                      : "border-[color:var(--line)]",
            ].join(" ")}
        >
            <div className="flex items-start gap-[12px]">
                <SourceLogo logo={source.logo} muted={setup} />
                <div className="grid min-w-0 flex-1 gap-[2px]">
                    <h3
                        id={`data-source-${source.key}-title`}
                        className="m-0 text-[14px] font-medium leading-[1.2] text-[color:var(--ink)]"
                    >
                        {source.name}
                    </h3>
                    <span className="text-[12.5px] leading-[1.4] text-[color:var(--muted)]">{source.blurb}</span>
                </div>
                <StatusPill tone={health.tone} label={health.label} />
            </div>
            {health.detail ? (
                <p
                    className={`m-0 text-[12.5px] leading-[1.45] ${health.tone === TONE.ATTENTION ? "text-[color:var(--danger)]" : "text-[color:var(--muted)]"}`}
                >
                    {health.detail}
                </p>
            ) : null}
            {loading ? (
                <SkeletonLines lines={2} label="Loading…" />
            ) : data == null ? null : setup ? (
                <SetupPreview sourceKey={source.key} />
            ) : (
                <SourceFacts sourceKey={source.key} data={data} />
            )}
            <div className="mt-auto flex items-center justify-end gap-[8px] border-t border-[color:var(--line-soft)] pt-[12px]">
                {health.tone === TONE.UNAVAILABLE ? (
                    <Button onClick={onRetry}>Retry</Button>
                ) : (
                    <Button
                        variant={setup || needsWork ? "primary" : "secondary"}
                        onClick={onOpen}
                        disabled={loading}
                        aria-label={`${setup ? "Set up" : needsWork ? "Continue setup for" : "Settings for"} ${source.name}`}
                    >
                        {setup ? "Set up" : needsWork ? "Continue setup" : "Settings"}
                    </Button>
                )}
            </div>
        </article>
    );
}

export default function HealthView({ sources, entries, directory, onOpen, onRetry }) {
    const healthByKey = Object.fromEntries(
        sources.map((source) => [source.key, sourceHealth(source.key, entries[source.key], directory)]),
    );
    const attention = sources.filter((source) => healthByKey[source.key].tone === TONE.ATTENTION);
    return (
        <div className="grid gap-[14px]">
            {attention.length > 0 ? (
                <div
                    role="status"
                    className="grid gap-[8px] rounded-[10px] border border-[color-mix(in_srgb,var(--danger)_45%,transparent)] bg-[color-mix(in_srgb,var(--danger)_8%,transparent)] px-[12px] py-[10px]"
                >
                    {attention.map((source) => (
                        <div key={source.key} className="flex flex-wrap items-center justify-between gap-[10px]">
                            <span className="text-[13px] leading-[1.4] text-[color:var(--ink)]">
                                <span className="font-medium">{source.name}:</span>{" "}
                                {healthByKey[source.key].label.toLowerCase()}.{" "}
                                <span className="text-[color:var(--muted)]">{healthByKey[source.key].detail}</span>
                            </span>
                            <Button size="sm" onClick={() => onOpen(source.key)}>
                                Continue setup
                            </Button>
                        </div>
                    ))}
                </div>
            ) : null}
            <div className="grid gap-[14px] md:grid-cols-2">
                {sources.map((source) => (
                    <HealthCard
                        key={source.key}
                        source={source}
                        entry={entries[source.key]}
                        health={healthByKey[source.key]}
                        onOpen={() => onOpen(source.key)}
                        onRetry={() => onRetry(source.key)}
                    />
                ))}
            </div>
        </div>
    );
}
